import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import styles from './BatchPipelineTool.module.css';

/* ────────── constants ────────── */
const TRANSLATE_MODEL = 'gpt-5-nano';
const NOTES_MODEL = 'gpt-5-nano';
const TRANSLATE_CHUNK_SIZE = 20;
const TRANSLATE_CONCURRENCY = 100;
const POLL_MS = 3000;

const STEP_KEYS = ['translate', 'notes'];
const STEP_LABELS = {
  translate: '翻译 (OpenAI)',
  notes: 'AI 笔记',
};

const initialStepState = () => ({
  status: 'idle',
  progress: 0,
  detail: '',
});

/* ────────── helpers ────────── */
function ts() {
  return new Date().toLocaleTimeString('zh-CN', { hour12: false });
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

/* ────────── component ────────── */
function BatchTextTool() {
  /* ── volumes / chapters ── */
  const [volumes, setVolumes] = useState([]);
  const [volumesLoading, setVolumesLoading] = useState(false);
  const [selectedVolumeId, setSelectedVolumeId] = useState('');

  /* ── pipeline state ── */
  const [running, setRunning] = useState(false);
  const [steps, setSteps] = useState(() =>
    Object.fromEntries(STEP_KEYS.map((k) => [k, initialStepState()])),
  );
  const [logs, setLogs] = useState([]);
  const [globalError, setGlobalError] = useState('');
  const cancelledRef = useRef(false);
  const logEndRef = useRef(null);

  /* ── derived ── */
  const selectedVolume = useMemo(
    () => volumes.find((v) => String(v.id) === String(selectedVolumeId)) || null,
    [volumes, selectedVolumeId],
  );

  const chaptersInVolume = useMemo(() => {
    if (!selectedVolume || !Array.isArray(selectedVolume.chapters)) return [];
    return [...selectedVolume.chapters].sort(
      (a, b) => (a.order_index ?? 0) - (b.order_index ?? 0),
    );
  }, [selectedVolume]);

  const chapterIds = useMemo(
    () => chaptersInVolume.map((c) => c.id),
    [chaptersInVolume],
  );

  /* ── log helper ── */
  const addLog = useCallback((text, type = 'info') => {
    setLogs((prev) => [...prev, { time: ts(), text, type }]);
  }, []);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  /* ── load data ── */
  const loadVolumes = useCallback(async () => {
    setVolumesLoading(true);
    try {
      const { data } = await api.get('/volumes/', {
        params: { limit: 500, include_chapters: true },
      });
      const list = Array.isArray(data) ? data : [];
      setVolumes(list);
      setSelectedVolumeId((prev) => {
        if (prev && list.some((v) => String(v.id) === String(prev))) return prev;
        return list.length > 0 ? String(list[0].id) : '';
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || '未知错误';
      console.error('[BatchTextTool] loadVolumes error:', err);
      addLog(`获取卷列表失败：${detail}`, 'error');
    } finally {
      setVolumesLoading(false);
    }
  }, [addLog]);

  useEffect(() => {
    loadVolumes();
  }, [loadVolumes]);

  /* ── step helpers ── */
  const updateStep = useCallback((key, patch) => {
    setSteps((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }, []);

  const resetSteps = useCallback(() => {
    setSteps(Object.fromEntries(STEP_KEYS.map((k) => [k, initialStepState()])));
  }, []);

  /* ────────────────────────────────────────────────
     Step 1: Translation (async polling)
  ──────────────────────────────────────────────── */
  const runTranslation = useCallback(
    async (ids) => {
      updateStep('translate', { status: 'running', progress: 0, detail: '正在启动翻译任务…' });
      addLog(`[翻译] 开始翻译 ${ids.length} 个章节`);

      const payload = {
        chapter_ids: ids,
        target_language: 'English',
        model: TRANSLATE_MODEL,
        chunk_size: TRANSLATE_CHUNK_SIZE,
        max_concurrent_requests: TRANSLATE_CONCURRENCY,
      };

      const { data } = await api.post('/openai-translate/batch', payload, { timeout: 60000 });
      if (!data?.task_id) throw new Error('未能启动翻译任务');

      const taskId = data.task_id;
      addLog(`[翻译] 任务 ${taskId.slice(0, 8)} 已启动，轮询中…`);

      let consecutiveErrors = 0;
      const MAX_POLL_ERRORS = 5;

      while (true) {
        if (cancelledRef.current) throw new Error('用户取消');
        await sleep(POLL_MS);

        try {
          const { data: s } = await api.get(`/openai-translate/batch/status/${taskId}`, { timeout: 30000 });
          consecutiveErrors = 0;

          const pct = s.progress || 0;
          const detail = `${s.translated_sentences ?? 0}/${s.total_sentences ?? '?'} 句 — ${s.current_chapter_title || ''}`;
          updateStep('translate', { progress: pct, detail });

          if (s.status === 'completed') {
            addLog(`[翻译] 全部完成，成功 ${s.succeeded_chapters}/${s.total_chapters} 章`, 'success');
            updateStep('translate', { status: 'done', progress: 100, detail: `${s.succeeded_chapters}/${s.total_chapters} 章完成` });
            return;
          }
          if (s.status === 'failed') {
            throw new Error(s.error || '翻译失败');
          }
        } catch (pollErr) {
          if (pollErr.message && !pollErr.response) throw pollErr;

          consecutiveErrors += 1;
          addLog(`[翻译] 轮询出错 (${consecutiveErrors}/${MAX_POLL_ERRORS})：${pollErr?.response?.status || pollErr.message}`, 'error');

          if (consecutiveErrors >= MAX_POLL_ERRORS) {
            throw new Error(`轮询连续失败 ${MAX_POLL_ERRORS} 次，放弃等待（后端任务可能仍在运行）`);
          }
        }
      }
    },
    [addLog, updateStep],
  );

  /* ────────────────────────────────────────────────
     Step 2: AI Notes (sync, long wait)
  ──────────────────────────────────────────────── */
  const runNotes = useCallback(
    async (ids) => {
      updateStep('notes', { status: 'running', progress: 0, detail: '正在批量生成笔记…' });
      addLog(`[笔记] 开始为 ${ids.length} 个章节生成 AI 笔记`);

      const { data } = await api.post(
        '/ai-notes/batch',
        { chapter_ids: ids, model: NOTES_MODEL },
        { timeout: 0 },
      );

      const succeeded = data.succeeded ?? 0;
      const total = data.total_requested ?? ids.length;
      addLog(`[笔记] 完成，成功 ${succeeded}/${total} 章`, succeeded === total ? 'success' : 'error');
      updateStep('notes', {
        status: succeeded > 0 ? 'done' : 'failed',
        progress: 100,
        detail: `${succeeded}/${total} 章完成`,
      });

      if (succeeded === 0) throw new Error('所有章节笔记生成失败');
    },
    [addLog, updateStep],
  );

  /* ────────── 预检查：获取最新状态 ────────── */
  const fetchFreshChapters = useCallback(async (volumeId) => {
    const { data } = await api.get('/volumes/', {
      params: { limit: 500, include_chapters: true },
    });
    const list = Array.isArray(data) ? data : [];
    setVolumes(list);
    const vol = list.find((v) => String(v.id) === String(volumeId));
    if (!vol || !Array.isArray(vol.chapters)) return [];
    return [...vol.chapters].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
  }, []);

  /* ────────── pipeline ────────── */
  const startPipeline = useCallback(async () => {
    if (chapterIds.length === 0) {
      setGlobalError('所选卷没有章节');
      return;
    }

    setGlobalError('');
    resetSteps();
    setLogs([]);
    setRunning(true);
    cancelledRef.current = false;

    const volLabel = selectedVolume?.novel?.title
      ? `${selectedVolume.novel.title} — ${selectedVolume.title}`
      : selectedVolume?.title || selectedVolumeId;

    addLog(`===== 流水线启动 =====`);
    addLog(`卷: ${volLabel} | ${chapterIds.length} 章`);

    /* ── Step 0: 预检查数据库状态 ── */
    addLog(`[预检查] 正在获取章节最新状态…`);
    let freshChapters;
    try {
      freshChapters = await fetchFreshChapters(selectedVolumeId);
    } catch {
      freshChapters = chaptersInVolume;
      addLog(`[预检查] 获取最新数据失败，使用缓存数据`, 'error');
    }

    const freshIds = freshChapters.map((c) => c.id);

    const needTranslateIds = freshChapters.filter((c) => !c.has_content_trans).map((c) => c.id);
    const needNotesIds = freshChapters.filter((c) => !c.has_ai_notes).map((c) => c.id);

    addLog(`[预检查] 翻译：${freshIds.length - needTranslateIds.length}/${freshIds.length} 已完成，待处理 ${needTranslateIds.length} 章`);
    addLog(`[预检查] 笔记：${freshIds.length - needNotesIds.length}/${freshIds.length} 已完成，待处理 ${needNotesIds.length} 章`);

    /* ── 执行各步骤 ── */
    const pipelineSteps = [
      {
        key: 'translate',
        fn: needTranslateIds.length > 0
          ? () => runTranslation(needTranslateIds)
          : null,
        skipReason: needTranslateIds.length === 0 ? `全部 ${freshIds.length} 章已翻译` : null,
      },
      {
        key: 'notes',
        fn: needNotesIds.length > 0
          ? () => runNotes(needNotesIds)
          : null,
        skipReason: needNotesIds.length === 0 ? `全部 ${freshIds.length} 章已有笔记` : null,
      },
    ];

    for (const step of pipelineSteps) {
      if (cancelledRef.current) {
        updateStep(step.key, { status: 'skipped', detail: '已取消' });
        continue;
      }
      if (!step.fn) {
        updateStep(step.key, { status: 'done', progress: 100, detail: step.skipReason });
        addLog(`[${STEP_LABELS[step.key]}] 跳过 — ${step.skipReason}`, 'success');
        continue;
      }
      try {
        await step.fn();
      } catch (err) {
        const msg = err.message || '未知错误';
        updateStep(step.key, { status: 'failed', detail: msg });
        addLog(`[${STEP_LABELS[step.key]}] 失败: ${msg}，继续执行后续步骤…`, 'error');
      }
    }

    setRunning(false);
    addLog(`===== 流水线结束 =====`, 'success');
  }, [
    chapterIds,
    chaptersInVolume,
    selectedVolume,
    selectedVolumeId,
    resetSteps,
    addLog,
    updateStep,
    fetchFreshChapters,
    runTranslation,
    runNotes,
  ]);

  const handleStop = useCallback(() => {
    cancelledRef.current = true;
    addLog('用户请求停止…', 'error');
  }, [addLog]);

  /* ── render step helpers ── */
  const dotClass = (status) => {
    switch (status) {
      case 'running': return styles.stepDotRunning;
      case 'done': return styles.stepDotDone;
      case 'failed': return styles.stepDotFailed;
      case 'skipped': return styles.stepDotSkipped;
      default: return styles.stepDotIdle;
    }
  };

  const dotIcon = (status, idx) => {
    switch (status) {
      case 'done': return '✓';
      case 'failed': return '✕';
      case 'skipped': return '—';
      default: return idx + 1;
    }
  };

  const statusText = (status) => {
    switch (status) {
      case 'idle': return '等待';
      case 'running': return '进行中';
      case 'done': return '完成';
      case 'failed': return '失败';
      case 'skipped': return '跳过';
      default: return '';
    }
  };

  const statusClass = (status) => {
    switch (status) {
      case 'running': return styles.stepStatusRunning;
      case 'done': return styles.stepStatusDone;
      case 'failed': return styles.stepStatusFailed;
      default: return '';
    }
  };

  /* ────────── render ────────── */
  return (
    <div className={styles.pipeline}>
      {/* ── Config ── */}
      <div className={styles.configCard}>
        <h2 className={styles.configTitle}>翻译 + 笔记流水线</h2>
        <p className={styles.configHint}>
          选择卷后，自动依次执行：AI 翻译 → AI 笔记生成（已完成的步骤会自动跳过）
        </p>

        {/* Volume */}
        <div className={styles.fieldGroup}>
          <label className={styles.label} htmlFor="textPipeVol">
            选择卷
            {chaptersInVolume.length > 0 && (
              <span className={styles.labelHint}>共 {chaptersInVolume.length} 章</span>
            )}
          </label>
          <select
            id="textPipeVol"
            className={styles.select}
            value={selectedVolumeId}
            onChange={(e) => setSelectedVolumeId(e.target.value)}
            disabled={volumesLoading || running}
          >
            {volumes.length === 0 && <option value="">暂无数据</option>}
            {volumes.map((v) => (
              <option key={v.id} value={String(v.id)}>
                {v.novel?.title ? `${v.novel.title} — ${v.title}` : v.title || `卷 ${v.id}`}
              </option>
            ))}
          </select>
        </div>

        {/* Chapter summary */}
        {chaptersInVolume.length > 0 && (
          <div className={styles.chapterSummary}>
            <span>章节：{chaptersInVolume.map((c) => c.title || `#${c.id}`).join('、')}</span>
          </div>
        )}

        {globalError && <div className={styles.errorMessage}>{globalError}</div>}

        {/* Actions */}
        <div className={styles.actionRow}>
          {!running ? (
            <button
              type="button"
              className={styles.startBtn}
              disabled={volumesLoading || chapterIds.length === 0}
              onClick={startPipeline}
            >
              开始处理
            </button>
          ) : (
            <button type="button" className={styles.stopBtn} onClick={handleStop}>
              停止
            </button>
          )}
        </div>
      </div>

      {/* ── Steps ── */}
      <div className={styles.stepsCard}>
        <h3 className={styles.stepsTitle}>流水线进度</h3>
        {STEP_KEYS.map((key, idx) => {
          const st = steps[key];
          return (
            <div key={key} className={styles.stepItem}>
              {/* indicator */}
              <div className={styles.stepIndicatorCol}>
                <div className={`${styles.stepDot} ${dotClass(st.status)}`}>
                  {dotIcon(st.status, idx)}
                </div>
                {idx < STEP_KEYS.length - 1 && (
                  <div className={`${styles.stepLine} ${st.status === 'done' ? styles.stepLineDone : ''}`} />
                )}
              </div>

              {/* content */}
              <div className={styles.stepContent}>
                <div className={styles.stepHeader}>
                  <span className={styles.stepName}>{STEP_LABELS[key]}</span>
                  <span className={`${styles.stepStatusText} ${statusClass(st.status)}`}>
                    {statusText(st.status)}
                  </span>
                </div>

                {st.detail && <div className={styles.stepDetail}>{st.detail}</div>}

                {(st.status === 'running' || st.status === 'done') && (
                  <div className={styles.progressBarWrapper}>
                    <div
                      className={`${styles.progressBarFill} ${st.status === 'done' ? styles.progressBarFillDone : ''}`}
                      style={{ width: `${st.progress}%` }}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Logs ── */}
      <div className={styles.logCard}>
        <div className={styles.logHeader}>
          <h3 className={styles.logTitle}>日志</h3>
          {logs.length > 0 && (
            <button type="button" className={styles.logClearBtn} onClick={() => setLogs([])}>
              清空
            </button>
          )}
        </div>
        <div className={styles.logBody}>
          {logs.length === 0 && <div style={{ color: 'var(--gray-40)' }}>暂无日志</div>}
          {logs.map((entry, i) => (
            <div key={i} className={styles.logEntry}>
              <span className={styles.logTime}>{entry.time}</span>
              <span
                className={`${styles.logText} ${
                  entry.type === 'error' ? styles.logError : entry.type === 'success' ? styles.logSuccess : ''
                }`}
              >
                {entry.text}
              </span>
            </div>
          ))}
          <div ref={logEndRef} />
        </div>
      </div>
    </div>
  );
}

export default BatchTextTool;

